export function parseProductNumber(value) {
    if (typeof value === 'number') {
        return value;
    }
    let normalized = String(value ?? '').trim().replace(/\s/g, '');
    if (normalized.includes(',') && normalized.includes('.')) {
        normalized = normalized.lastIndexOf(',') > normalized.lastIndexOf('.')
            ? normalized.replace(/\./g, '').replace(',', '.')
            : normalized.replace(/,/g, '');
    } else {
        normalized = normalized.replace(',', '.');
    }
    return normalized === '' ? NaN : Number(normalized);
}

export function normalizeProductText(value) {
    return String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
}

export function getProductLines(product = {}) {
    const source = Array.isArray(product.subproducts) && product.subproducts.length > 0
        ? product.subproducts
        : [{
            name: product.description || product.details || product.name || '',
            version: product.version || '',
            units: product.units || 1,
            price: product.price ?? 0
        }];

    return source.map((line) => ({
        name: String(line?.name || '').trim(),
        version: String(line?.version || '').trim(),
        units: line?.units ?? 1,
        price: line?.price ?? ''
    }));
}

export function getProductDetails(product = {}) {
    const firstSubproduct = Array.isArray(product.subproducts) ? product.subproducts[0] : null;
    const detailsMatchFirstLine = firstSubproduct && normalizeProductText(product.details) === normalizeProductText(firstSubproduct.name);
    const descriptionIsDifferent = normalizeProductText(product.description) !== normalizeProductText(firstSubproduct?.name);
    return String(detailsMatchFirstLine && descriptionIsDifferent ? product.description : product.details || '').trim();
}

export function normalizeProductLines(lines) {
    return lines.map((line) => ({
        name: String(line?.name || '').trim(),
        version: String(line?.version || '').trim(),
        units: parseProductNumber(line?.units),
        price: String(line?.price ?? '').trim() === '' ? 0 : parseProductNumber(line?.price)
    }));
}

export function productLinesAreValid(lines, allowMissingPrice = false) {
    return Array.isArray(lines) && lines.length > 0 && lines.every((line) => {
        const units = parseProductNumber(line?.units);
        const price = parseProductNumber(line?.price);
        const missingPrice = String(line?.price ?? '').trim() === '';
        return String(line?.name || '').trim() !== '' &&
            Number.isInteger(units) && units > 0 &&
            ((allowMissingPrice && missingPrice) || (Number.isFinite(price) && price >= 0));
    });
}

export function getProductTotal(productOrLines) {
    const lines = Array.isArray(productOrLines) ? productOrLines : getProductLines(productOrLines);
    return lines.reduce((total, line) => {
        const units = parseProductNumber(line.units);
        const price = parseProductNumber(line.price);
        return total + (Number.isFinite(units) && Number.isFinite(price) ? units * price : 0);
    }, 0);
}

export function getLegacyCommercialFields(lines) {
    const first = normalizeProductLines(lines)[0];
    return {
        description: first?.name || '',
        version: first?.version || '',
        units: first?.units ?? 1,
        price: first?.price ?? 0
    };
}

export function renameSingleProductLine(product, name) {
    const lines = getProductLines(product);
    return lines.length === 1 ? [{ ...lines[0], name: String(name ?? '').trim() }] : null;
}

export function replaceMatchingProductLines(product, sourceName, sourceVersion, nextName, nextVersion) {
    const expectedName = String(sourceName ?? '').trim();
    const expectedVersion = String(sourceVersion ?? '').trim();
    return getProductLines(product).map((line) => {
        if (line.name !== expectedName || line.version !== expectedVersion) {
            return { ...line };
        }
        return {
            ...line,
            name: String(nextName ?? '').trim(),
            version: String(nextVersion ?? '').trim()
        };
    });
}

export function replaceProductHistoryEntry(history, index, entry) {
    return history.map((current, currentIndex) => currentIndex === index ? entry : current);
}

export function removeProductHistoryEntry(history, index) {
    return history.filter((_, currentIndex) => currentIndex !== index);
}

export function productLinesMatchText(product, text) {
    const query = normalizeProductText(text);
    return getProductLines(product).some((line) =>
        [line.name, line.version, line.units, line.price].some((value) => normalizeProductText(value).includes(query))
    );
}

export function productLinesMatchFilters(product, names = [], versions = []) {
    const selectedNames = new Set(names.map(normalizeProductText).filter(Boolean));
    if (selectedNames.size === 0) {
        return true;
    }
    const matchingLines = getProductLines(product).filter((line) => selectedNames.has(normalizeProductText(line.name)));
    const selectedVersions = new Set(versions.map(normalizeProductText).filter(Boolean));
    return matchingLines.length > 0 && (selectedVersions.size === 0 || matchingLines.some((line) => selectedVersions.has(normalizeProductText(line.version))));
}

export function productMatchesText(product, text) {
    const query = normalizeProductText(text);
    const fields = ['name', 'notes', 'details', 'description', 'id', 'customerNotes', 'statusReason'];
    if (fields.some((field) => normalizeProductText(product?.[field]).includes(query)) || productLinesMatchText(product, query)) {
        return true;
    }
    return (product?.history || []).some((history) =>
        fields.some((field) => normalizeProductText(history?.[field]).includes(query)) || productLinesMatchText(history, query)
    );
}
